import { z } from 'zod';

const emailField = z
  .string()
  .trim()
  .min(1, 'メールアドレスは必須です。')
  .max(100, 'メールアドレスは100文字以下にしてください。')
  .regex(
    /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/,
    'メールアドレスを正しいメールアドレスにしてください。'
  );

const nameField = z
  .string()
  .trim()
  .min(1, '管理者名は必須です。')
  .max(20, '管理者名は20文字以下で入力してください。');

const manageSiteField = z
  .string({
    required_error: 'サイト区分は必須です。',
    invalid_type_error: 'サイト区分は必須です。',
  })
  .min(1, 'サイト区分は必須です。');

/**
 * Portal codes, matching AdminSsoGrant::SYSTEMS on the backend and the P1/P2/P3 vocabulary the
 * rest of the SSO estate already uses. Labels live in AdminUserSsoFields.
 */
export const SSO_SYSTEMS = ['P2', 'P3'] as const;

export type SsoSystem = (typeof SSO_SYSTEMS)[number];

const ssoSystemsField = z.array(z.enum(SSO_SYSTEMS)).default([]);

/**
 * ログインID is only required once a system is ticked. Checked at object level rather than on the
 * field, because the condition depends on another field.
 */
/**
 * No `.or(z.literal(''))`: that turns the field into a union, and when both members fail zod
 * reports `invalid_union` with its own English default — so typing `12a` showed `Invalid input`
 * instead of the Japanese message written right here. An empty string already passes the regex.
 */
const ssoLoginIdField = z
  .string()
  .trim()
  .max(6, '6桁以内の数字を入力してください。')
  .regex(/^[0-9]*$/, '6桁以内の数字を入力してください。')
  .optional();

/**
 * Trần độ dài mật khẩu KHI đã tick hệ thống SSO — nay bằng đúng trần thường (BD「SSO Admin
 * tenant」R18: パスワードは72文字以下).
 *
 * TRƯỚC 28/08/2026 CHỖ NÀY LÀ 8, và đó chưa bao giờ là luật nghiệp vụ: `m_tantosha.TANTO_PWD` của
 * P2 khi ấy là `varchar(8)` so sánh plaintext, dài hơn sẽ bị MySQL cắt âm thầm thành mật khẩu
 * KHÁC. Migration `2026_08_27_000001` đã nới cột thành `varchar(255)` và P2 hash khi nhận, nên
 * ràng buộc đó biến mất; giữ 8 chỉ còn là chặt hơn spec 9 lần mà không có lý do.
 *
 * GIỮ HẰNG SỐ NÀY DÙ ĐÃ BẰNG TRẦN THƯỜNG: nó là điểm neo để đổi khi khách chốt luật ký tự cho
 * Cognito (pool thật đòi ≥8 và đủ hoa/thường/số/ký hiệu) và cho cổng charset của P3
 * (`[0-9a-zA-Z@+/!-]`). Hai thứ đó BD chưa nói, và đều chặt hơn con số này.
 */
export const SSO_PASSWORD_MAX = 72;

const capPasswordWhenLinked = (
  data: { sso_systems?: string[]; password?: string },
  ctx: z.RefinementCtx
) => {
  const password = data.password?.trim() ?? '';

  if ((data.sso_systems ?? []).length > 0 && password.length > SSO_PASSWORD_MAX) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['password'],
      message: `SSO連携する場合、パスワードは${SSO_PASSWORD_MAX}文字以内で入力してください。`,
    });
  }
};

const requireLoginIdWhenLinked = (
  data: { sso_systems?: string[]; sso_login_id?: string },
  ctx: z.RefinementCtx
) => {
  if ((data.sso_systems ?? []).length > 0 && !data.sso_login_id?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['sso_login_id'],
      message: 'ログインIDは必須です。',
    });
  }
};

export const adminUserFormSchema = z.object({
  name: nameField,
  email: emailField,
  password: z
    .string()
    .trim()
    .max(72, 'パスワードは72文字以下にしてください。')
    .optional()
    .or(z.literal('')),
  manage_site: manageSiteField,
  sso_systems: ssoSystemsField,
  sso_login_id: ssoLoginIdField,
})
  .superRefine(requireLoginIdWhenLinked)
  .superRefine(capPasswordWhenLinked);

export const isSsoPasswordRequired = (
  selectedSystems: readonly string[] = [],
  passwordOptionalSystems: readonly string[] = [],
  activeSystems: readonly string[] = [],
) => selectedSystems.some(system => !passwordOptionalSystems.includes(system) && !activeSystems.includes(system));

export const createAdminUserEditFormSchema = (
  passwordOptionalSystems: readonly string[],
  initialManageSite?: string,
  activeSystems: readonly string[] = [],
) =>
  adminUserFormSchema.superRefine((data, ctx) => {
    const tenantChanged = initialManageSite && data.manage_site !== initialManageSite;
    if (isSsoPasswordRequired(
      data.sso_systems, tenantChanged ? [] : passwordOptionalSystems, tenantChanged ? [] : activeSystems,
    ) && !data.password?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['password'],
        message: 'パスワードは必須です。',
      });
    }
  });

export const adminUserCreateFormSchema = z.object({
  name: nameField,
  email: emailField,
  password: z
    .string()
    .trim()
    .min(1, 'パスワードは必須です。')
    .max(72, 'パスワードは72文字以下にしてください。'),
  manage_site: manageSiteField,
  sso_systems: ssoSystemsField,
  sso_login_id: ssoLoginIdField,
})
  .superRefine(requireLoginIdWhenLinked)
  .superRefine(capPasswordWhenLinked);

export type AdminUserFormData = z.infer<typeof adminUserFormSchema>;
